// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';
import camelCase from 'lodash/camelCase';
import { LocationDescriptorObject } from 'history';

// Components
import { IntegerValue } from '../../../components/common';
import { IconButton, PrimaryButton } from '../../../components/buttons';
import SavedItemsTable from './SavedItemsTable';
import Tooltip from '../../../components/tooltips/Tooltip';
import ShelfStatus from '../../../components/shelf-management/ShelfStatus';

// Selectors
import { selectSavedFundsShelfStatusedFund } from '../../../../redux/modules/shelf-management/selectors';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { getSavedFunds, toggleSavedFund } from '../../../../redux/modules/funds/saved-funds';
import { addFundsToComparisonById, ADD_FUNDS_TO_COMPARISON } from '../../../../redux/modules/comparison';
import { addFundsToPortfolio, ADD_FUNDS_TO_PORTFOLIO, resetPortfolio } from '../../../../redux/modules/edit-portfolio';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../../utils/3rd-party/heap';
import { formatMessage, FormattedMessage, formatNumber, formatLocalizedString } from '../../../../utils/i18n';
import { searchObjectsOnFields } from '../../../../utils/search';
import { PillLabel } from '../../../components/misc';
import { NAV_PATHS } from '../../../../utils/constants';
import Fund from '@/types/fund';

const roundValue =
  (digits: number) =>
  ({ value }: any) => <span>{value ? formatNumber(value, digits) : '-'}</span>;

type DispatchProps = {
  getSavedFunds: Function;
  toggleSavedFund: Function;
  addFundsToComparisonById: (ids: string[]) => Promise<void[]>;
  addFundsToPortfolio: Function;
  resetPortfolio: Function;
};

type StateProps = {
  funds: Fund[];
  loading: boolean;
  addingToPortfolio: boolean;
  addingToComparison: boolean;
};

type Props = RouteComponentProps &
  DispatchProps &
  StateProps & {
    query?: string;
  };

interface State {
  selected: Fund[];
  redirect?: string | LocationDescriptorObject;
}

class SavedFunds extends React.Component<Props, State> {
  readonly state: State = {
    selected: [],
  };

  readonly columns: any[] = [
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.FundName')}</span>,
      accessor: 'names.full',
      className: 'fund-name',
      minWidth: 245,
      Cell: ({ original, value }: { original: Fund; value: string }) => (
        <>
          <Tooltip message={value}>
            <span>{value}</span>
          </Tooltip>
          {original.flags && <ShelfStatus statuses={original.flags} />}
        </>
      ),
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.FundType')}</span>,
      accessor: 'universe',
      className: 'fund-type',
      Cell: ({ value }: { value: string }) => (
        <Tooltip message={value}>
          <PillLabel text={value} />
        </Tooltip>
      ),
      minWidth: 105,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.Classification')}</span>,
      accessor: 'classification',
      minWidth: 140,
      Cell: ({ value }: { value: string }) => (
        <Tooltip message={value}>
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.INC')}</span>,
      accessor: 'performance.percentGrowth.INC',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      maxWidth: 80,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.1Y')}</span>,
      accessor: 'performance.percentGrowth.1Y',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      maxWidth: 80,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.3Y')}</span>,
      accessor: 'performance.percentGrowth.3Y',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      maxWidth: 80,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.5Y')}</span>,
      accessor: 'performance.percentGrowth.5Y',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      maxWidth: 80,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.MER')}</span>,
      accessor: 'mer',
      Cell: roundValue(2),
      maxWidth: 80,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.Beta')}</span>,
      accessor: 'performance.beta.3Y',
      Cell: roundValue(2),
      maxWidth: 80,
    },
    {
      Header: () => <span>{formatMessage('Funds.Table.Headers.StdDev')}</span>,
      accessor: 'performance.standardDeviation.3Y',
      Cell: roundValue(2),
      maxWidth: 80,
    },
    {
      accessor: '_id',
      className: 'cell-icon',
      Cell: ({ original }: { original: Fund }) => <IconButton icon="ds-delete" onClick={() => this.toggleSaved(original)} />,
      width: 48,
    },
  ];

  onAddToPortfolio = () => {
    this.props.resetPortfolio();
    this.props.addFundsToPortfolio(this.state.selected).then(() => {
      this.setState({ redirect: '/models?newPortfolio=true' });
    });
  };

  onAddToComparison = () => {
    const { location } = this.props;
    const ids = this.state.selected.map(f => f._id);
    this.props.addFundsToComparisonById(ids).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  getActions = () => {
    const { addingToPortfolio, addingToComparison } = this.props;
    return [
      <PrimaryButton key="action-add" loading={addingToPortfolio} compact icon="plus" onClick={this.onAddToPortfolio}>
        <FormattedMessage id={`Search.Funds.${addingToPortfolio ? 'AddingToPortfolio' : 'AddToPortfolio'}`} />
      </PrimaryButton>,
      <PrimaryButton key="action-compare" loading={addingToComparison} compact icon="plus" onClick={this.onAddToComparison}>
        <FormattedMessage id={`Search.Funds.${addingToComparison ? 'AddingToComparison' : 'AddToComparison'}`} />
      </PrimaryButton>,
    ];
  };

  onSelectSavedFunds = (ids: string[]) => {
    const selected = this.props.funds.filter(f => ids.includes(f._id));

    this.setState(prevState => ({
      selected: [...prevState.selected, ...selected],
    }));
  };

  onUnselectSavedFunds = (ids: string[]) => {
    this.setState(prevState => ({
      selected: prevState.selected.filter(f => !ids.includes(f._id)),
    }));
  };

  getFilteredFunds = () => {
    const { query, funds } = this.props;

    const data = this.formatData(funds);

    if (!query) {
      return data;
    }

    return searchObjectsOnFields(query, data, ['names.short', 'names.full', 'symbols', 'classification', 'universe']);
  };

  getDisclaimers = () => [formatMessage('Disclaimers.NET_PERFORMANCE'), formatMessage('Disclaimers.CompareFundsForMoreInfo')];

  toggleSaved = (row: Fund) => {
    const fund = this.props.funds.find(f => f._id === row._id);
    if (!fund) {
      return;
    }

    this.props.toggleSavedFund(fund);

    this.setState(prevState => ({
      selected: prevState.selected.filter(f => f !== row),
    }));
  };

  formatData = (funds: Fund[]) =>
    funds.map(fund => ({
      ...fund,
      names: {
        full: formatLocalizedString(fund.names.full),
        short: formatLocalizedString(fund.names.short),
      },
      classification: formatLocalizedString(fund.classification),
      objective: formatLocalizedString(fund.objective),
      assetType: fund.assetType ? formatLocalizedString(fund.assetType) : '',
      universe: fund.universe ? formatLocalizedString(fund.universe) : '',
    }));

  redirectToSearch = () => this.setState({ redirect: '/search/funds' });

  render() {
    const { loading, query } = this.props;
    const { selected, redirect } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    return (
      <SavedItemsTable
        className="saved-funds"
        data={this.getFilteredFunds()}
        columns={this.columns}
        actions={this.getActions()}
        loading={loading}
        selected={selected}
        query={query}
        onSelectItems={this.onSelectSavedFunds}
        onUnselectItems={this.onUnselectSavedFunds}
        emptyResultsAction={this.redirectToSearch}
        emptyResultsActionLabel={formatMessage('Search.SavedFunds.NoData.GetStarted')}
        emptyResultsActionIcon="icon-arrow-up-right2"
        noDataTitle={formatMessage('Search.SavedFunds.NoData.Title')}
        noDataBodyText={formatMessage('Search.SavedFunds.NoData.Text')}
        noResultsTitle={formatMessage('Search.NoSearchResults.Title')}
        noResultsBodyText={formatMessage('Search.NoSearchResults.Text')}
        selectedTitle={formatMessage('Search.Funds.SelectedTitle', { count: selected.length })}
        disclaimers={this.getDisclaimers()}
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  funds: selectSavedFundsShelfStatusedFund(state.funds.savedFunds).funds,
  loading: state.funds.savedFunds.loading,
  addingToPortfolio: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  addingToComparison: selectIsLoadingEvery(state, [ADD_FUNDS_TO_COMPARISON.type]),
});

export default connect(mapStateToProps, {
  getSavedFunds,
  toggleSavedFund,
  addFundsToComparisonById,
  addFundsToPortfolio,
  resetPortfolio,
})(SavedFunds);
